import { Component, OnInit, HostListener, OnDestroy } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { ConstantsService, SessionService, LookupsService, EnvironmentService, MessageTypeEnum } from '@app/base/core';
import {
  triggerFormValidation, UspsService, compareObjects, focusFirstInvalidField,
  scrollToHeader, getFormValidators
} from '@app/base/shared';
import { SocoHttpService, SocoModalService } from '@app/base/soco';
import { LandingService } from '@app/modules/landing/base';
import { Observable, of, Subscription } from 'rxjs';
import { AlertService } from '@app/base/alerts/alert.service';
import { DatePipe } from '@angular/common';

declare var swal: any;
require('sweetalert');

@Component({
  selector: 'app-letter',
  templateUrl: './letter.component.html',
  styleUrls: ['./letter.component.css']
})
export class LetterComponent implements OnInit, OnDestroy {

  messageTypeEnum: typeof MessageTypeEnum = MessageTypeEnum;
  formValidation: any[];
  lookupsLoaded = false; // Hide UI unless common lookups is loaded.

  // Control the active view: map or letter form
  activeViewEnum: typeof LetterActiveViewEnum = LetterActiveViewEnum;
  gasLetterActiveView: LetterActiveViewEnum = LetterActiveViewEnum.Location;

  serviceAddress: any;  // Hold service address
  mapLocationDetails: {}; // Input location details to map component
  addressToValidate: { street: any; city: any; state: any; zip: any; };
  gasLetterForm: FormGroup;
  validatedAddress: any = null;
  skipCanDeactivate = false;
  popupOpen = false;
  addressValidationError: any;
  isPoBox = false;
  addressValidationModalId = '#address_validation_modal';
  sryNotification: any;
  message: any;
  alertMsg: any = [];
  private zipSubscription: Subscription;
  private addressSubscription: Subscription;
  private gasLetterSubscription: Subscription;
  private postLetterSubscription: Subscription;

  @HostListener('window:beforeunload', ['$event']) onBeforeUnload(event) {
    if (this.gasLetterActiveView !== this.activeViewEnum.Location && this.socoHttp.sessionActive) {
      event.preventDefault();
      // Specially for chrome...
      event.returnValue = this.constantsSvc.changes;
    }
  }

  //#region > CTOR <
  constructor(
    private router: Router,
    public environmentSvc: EnvironmentService,
    public constantsSvc: ConstantsService,
    public lookupSvc: LookupsService,
    public sessionSvc: SessionService,
    private landingSvc: LandingService,
    private socoModalService: SocoModalService,
    private usps: UspsService, private alertService: AlertService, private datePipe: DatePipe,
    private socoHttp: SocoHttpService) { this.buildFormGroup(); }
  //#endregion
  //#region > Events <

  // used to close opened pop ups and unsubscribe to services.
  ngOnDestroy() {
    if ($('.swal-modal').is(':visible')) {
      swal.close();
    }
    if ($('#address_validation_modal').is(':visible')) {
      $('#address_validation_modal').modal('hide');
    }
    if ($('#acknowledge_validation_modal').is(':visible')) {
      $('#acknowledge_validation_modal').modal('hide');
    }
    if (this.zipSubscription) {
      this.zipSubscription.unsubscribe();
    }
    if (this.addressSubscription) {
      this.addressSubscription.unsubscribe();
    }
    if (this.gasLetterSubscription) {
      this.gasLetterSubscription.unsubscribe();
    }
    if (this.postLetterSubscription) {
      this.postLetterSubscription.unsubscribe();
    }
  }

  // used to subscribe to services and call the initial methods.
  ngOnInit() {
  if ($('.swal-modal').is(':visible')) {
    swal.close();
  }
  if ($('#soco-modal').is(':visible')) {
    $('#soco-modal').modal('hide');
  }

    const subscribe = this.lookupSvc.commonLookupsLoaded$.subscribe(val => {
      if (subscribe && val) {
        subscribe.unsubscribe();
      }
      if (val) {
        this.alertMsg = (this.lookupSvc.bannerMessages.find(x => x.itemName === 'Gas Letter Request'
        ));
        this.alertService.info(this.alertMsg.message);
      }
    });
    const sub = this.lookupSvc.commonLookupsLoaded$.subscribe(val => {
      if (sub && val) { sub.unsubscribe(); }
      this.lookupsLoaded = val;
      if (val) {
      // SryNotification variable is to pass the custom error message from database to Custom-map.Component.ts
        this.sryNotification = this.lookupSvc.messages.filter(a => a.messageType === this.constantsSvc.error2);
      scrollToHeader();
      }
    });
    this.setActiveLocationDetails();
    this.resolveMailingZip();

    if (!this.environmentSvc.isSoCoProduction) {
      this.gasLetterSubscription = this.gasLetterForm.valueChanges.subscribe(val => {
        this.formValidation = getFormValidators(this.gasLetterForm);
      });
    }
  }

  // used to handle page refresh if refreshed or timeout
  canDeactivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot, nextState: RouterStateSnapshot): Observable<boolean> | boolean {
    if (this.gasLetterForm) {
      if (this.gasLetterActiveView === this.activeViewEnum.Location) { return of(true); }
      if (this.skipCanDeactivate) { return of(true); }
      this.showDiscardDialog(this.constantsSvc.showDiscardmsgMultistep, 'Stay', 'Discard Changes', true);
      const subscription = this.socoModalService.modalConfirmation$.subscribe(choice => {
        subscription.unsubscribe();
        if (!choice) {
          return true;
        }
      });
      return this.socoModalService.modalConfirmation$;
    } else {
      return true;
    }
  }

  // used for resolving zip code
  resolveMailingZip() {
    this.zipSubscription = this.gasLetterForm.controls['applicantZip'].valueChanges.subscribe(selectedValue => {
      if (this.gasLetterForm.get('mailingAddressSame').value) { return; }
      if (this.constantsSvc.zipCodePattern.test(selectedValue)) { // We have a valid ZIP Code.
        this.socoHttp.showPleaseWait('Processing.', 'ContainerAwaiter');
        this.usps.GetCityStateFromZip(selectedValue, (result) => {
          if (result.CityStateLookupResponse.ZipCode.Error) {
            this.gasLetterForm.patchValue({ applicantZip: null });
            this.socoHttp.hidePleaseWait('ContainerAwaiter');
            swal(this.constantsSvc.invalidZipSwal, '', this.constantsSvc.errorSwal);
            return;
          }
          this.lookupSvc.loadMailingAddressCityLookups(result.CityStateLookupResponse.ZipCode.State,
            result.CityStateLookupResponse.ZipCode.City);
          this.addressSubscription = this.lookupSvc.mailingAddressLookupsLoaded$.subscribe(loaded => {
            if (loaded) {
              this.socoHttp.hidePleaseWait('ContainerAwaiter');
              this.gasLetterForm.patchValue({
                applicantCity: this.lookupSvc.mailingCity.length > 0 ? this.lookupSvc.mailingCity[0] : null,
                applicantState: this.lookupSvc.mailingState.length > 0 ? this.lookupSvc.mailingState[0] : null,
              });
            }
          }, fail => { this.socoHttp.hidePleaseWait('ContainerAwaiter'); });
        });
      }
    });
  }

  setActiveLocationDetails() { // Set the location details either from map or active ldc info.
    this.mapLocationDetails = {
      lat: this.serviceAddress != null ? this.serviceAddress.latitude : this.sessionSvc.activeLdc.latitude,
      lng: this.serviceAddress != null ? this.serviceAddress.longitude : this.sessionSvc.activeLdc.longitude,
      zoom: this.serviceAddress != null ? this.serviceAddress.zoomLevel : this.sessionSvc.activeLdc.mapZoom,
      showMarker: Boolean(this.serviceAddress != null),
    };
  }

  // used to display dialog for discard option
  showDiscardDialog(body: string, cancelText: string, confirmText: string, isDanger: boolean) {
    this.socoModalService.confirm({
      title: 'Confirmation',
      body: body,
      headerCssClass: isDanger ? 'bg-danger' : 'bg-info',
      buttonCssClass: isDanger ? 'btn-danger' : 'btn-info',
      confirmButton: confirmText,
      cancelButton: cancelText
    });
  }

   // method to get service address
   getServiceAddress($event) { // Event fire by Map when address selected.
    if (!this.serviceAddress || !compareObjects(this.serviceAddress, $event)) {
      this.serviceAddress = $event;
      this.lookupSvc.loadServiceAddressLookups(this.serviceAddress.state.short_name, this.serviceAddress.city, this.serviceAddress.county);
      const subscription = this.lookupSvc.serviceAddressLookupsLoaded$.subscribe(isLoaded => {
        if (isLoaded) {
          if (subscription !== undefined) {
            subscription.unsubscribe();
          }
          this.setActiveView(LetterActiveViewEnum.LetterForm);
          this.gasLetterForm.patchValue({
            serviceCity: this.lookupSvc.serviceCity.find(x => x.itemName.toUpperCase() === this.serviceAddress.city.toUpperCase()),
            serviceState: this.lookupSvc.serviceState.find(x => x.id.toUpperCase() === this.serviceAddress.state.short_name.toUpperCase()),
            projectAddress: `${this.serviceAddress.street_number} ${this.serviceAddress.street_name}`,
            zip: this.serviceAddress.zip
          });

          // If user toggeled same as service address, then clone service
          if (this.gasLetterForm.controls['mailingAddressSame'].value) {
            this.cloneServiceAddress(false);
            this.gasLetterForm.patchValue({ mailingAddressSame: false });
          }
        }
      });
    } else { this.setActiveView(LetterActiveViewEnum.LetterForm); }
  }

  setActiveView(view: LetterActiveViewEnum) { // Switch view between map and form
    this.gasLetterActiveView = view;
    this.setActiveLocationDetails();
    scrollToHeader();
  }

  // Gas request letter form
  buildFormGroup(): void {
    this.gasLetterForm = new FormGroup({
      projectName: new FormControl(null),
      landDistrict: new FormControl(null),
      landLot: new FormControl(null),
      applicantProjectType: new FormControl(null, [Validators.required]),
      letterAddressedTo: new FormControl(null, [Validators.required, Validators.pattern(this.constantsSvc.spacePattern)]),
      zip: new FormControl({ value: null, disabled: true }, [Validators.required, Validators.pattern(this.constantsSvc.zipCodePattern)]),
      companyName: new FormControl(null, [Validators.pattern(this.constantsSvc.spacePattern)]),
      closestStreet: new FormControl(null),
      projectAddress: new FormControl({ value: null, disabled: true }, [Validators.required]),
      serviceCity: new FormControl({ value: null, disabled: true }, [Validators.required]),
      serviceState: new FormControl({ value: null, disabled: true }, [Validators.required]),
      isPoBox: new FormControl(),
      poBoxNumber: new FormControl(null, [Validators.pattern(this.constantsSvc.numericPatternwithZero)]),
      mailingAddressSame: new FormControl(false),
      mailingAddress: new FormControl(null, [Validators.required, Validators.pattern(this.constantsSvc.spacePattern)]),
      applicantCity: new FormControl({ value: null, disabled: true }, [Validators.required]),
      applicantState: new FormControl({ value: null, disabled: true }, [Validators.required]),
      applicantZip: new FormControl(null, [Validators.required, Validators.pattern(this.constantsSvc.zipCodePattern)]),

      contactName: new FormControl(null, [Validators.required, Validators.pattern(this.constantsSvc.spacePattern)]),
      contactPhoneNumber: new FormControl(null, [Validators.required, Validators.pattern(this.constantsSvc.phoneNumberPattern)]),
      applicantPhoneNumberExtension: new FormControl(null,
        [Validators.pattern(this.constantsSvc.phoneNumberExtensionPattern), Validators.maxLength(6)]),
      applicanteMailAddress: new FormControl(null, [Validators.pattern(this.constantsSvc.emailPattern)]),
      fax: new FormControl(null, [Validators.pattern(this.constantsSvc.phoneNumberPattern)]),

      specialNotes: new FormControl(null),
      serviceFormattedAddress: new FormControl(null),
      mailingFormattedAddress: new FormControl(null),
      sourceCd: new FormControl(null),
      recordCreateDate: new FormControl(null),
      recordCreateUserId: new FormControl(null),
      recordUpdateDate: new FormControl(null),
      recordUpdateUserId: new FormControl(null),
      countyId: new FormControl(null)
    },
      { updateOn: 'blur' }
    );
  }
  getAddressFromModal($event) {
    if (compareObjects(this.addressToValidate, $event) || this.gasLetterForm.controls['isPoBox'].value) {
      this.submitForm();
    } else {
      this.updateValidatedAddress(this.validatedAddress);
      this.submitForm();
    }
    $(this.addressValidationModalId).modal('hide');
  }
  modalChangeMailingAddress() {
    $(this.addressValidationModalId).modal('hide');
    this.gasLetterForm.patchValue({ mailingAddress: null, applicantZip: null, applicantCity: null, applicantState: null });
    this.cloneServiceAddress(false);
    this.gasLetterForm.get('mailingAddressSame').setValue(false);
    triggerFormValidation(this.gasLetterForm);
    this.gasLetterForm.updateValueAndValidity();
    focusFirstInvalidField(this.gasLetterForm);
  }
  updateValidatedAddress($event: any) {
    let newCity: any;
    let newState: any;
    this.gasLetterForm.patchValue({ mailingAddress: $event.street, applicantZip: $event.zip });
    // update the city and state if different from original address
    if (this.addressToValidate.city.toUpperCase() !== $event.city.toUpperCase()) {
      newCity = this.lookupSvc.mailingCity.find(i => i.itemName.toUpperCase() === $event.city.toUpperCase());
      this.gasLetterForm.patchValue({ applicantCity: newCity });
    }
    if (this.addressToValidate.state.toUpperCase() !== $event.state.toUpperCase()) {
      newState = this.lookupSvc.serviceState.find(i => i.id.toUpperCase() === $event.state.toUpperCase());
      this.gasLetterForm.patchValue({ applicantState: newState });
    }
    this.gasLetterForm.updateValueAndValidity();
  }

 // On submit
 clickedSubmit() {
    // skip address validation
    const poBoxChecked = this.gasLetterForm.controls['isPoBox'].value;
    if (this.gasLetterForm.valid && poBoxChecked) { this.submitForm(); }
    if (this.gasLetterForm.valid) {
      this.addressToValidate = this.getAddressObjFromForm();
      if (this.addressToValidate) {
        this.usps.GetValidatedAddress(this.addressToValidate, (result) => {
          if (result.Error) {
            this.addressValidationError = result.Error.Description;
          } else {
            this.validatedAddress = result;
          }

          if (compareObjects(this.addressToValidate, this.validatedAddress)) {
            this.submitForm();
          } else {
            $(this.addressValidationModalId).modal();
            $(this.addressValidationModalId).focus();
          }
        });
      }
    } else {
      triggerFormValidation(this.gasLetterForm);
      focusFirstInvalidField(this.gasLetterForm);
    }
  }

 private submitForm() {
  if ($('#acknowledge_validation_modal').is(':visible')) {
    this.popupOpen = true;
    $('#acknowledge_validation_modal').modal('hide');
    this.skipCanDeactivate = true;
  this.router.navigate(['/', this.sessionSvc.paramLdc.toLowerCase(), 'home']);
  }
  let countyId;
  if (this.lookupSvc.serviceCounty.length > 0 && this.serviceAddress.county) {
    const county =   this.lookupSvc.serviceCounty.filter(x => x.itemName.toUpperCase()
    === this.serviceAddress.county.toUpperCase())[0];
    countyId = county !== undefined ? county.id : '-1';
  } else {
    countyId = '-1';
  }
    if (this.gasLetterForm.valid && !this.popupOpen) {

      this.gasLetterForm.patchValue({
        serviceFormattedAddress: this.serviceAddress.formatted_address,
        mailingFormattedAddress: this.getMailingFormattedAddress(),
        sourceCd: 'Applicant',
        recordCreateDate: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss', this.constantsSvc.TimeZone),
        recordCreateUserId: 'Applicant',
        recordUpdateDate: this.datePipe.transform(new Date(), 'dd-MMM-yy HH:mm:ss', this.constantsSvc.TimeZone),
        recordUpdateUserId: 'Applicant',
        countyId: countyId
      });
      const formValue = this.gasLetterForm.getRawValue();
      formValue.isPoBox = formValue.isPoBox === true ? 'Y' : 'N';
      formValue.mailingAddress = formValue.isPoBox === 'Y' ? '' : formValue.mailingAddress;

      this.postLetterSubscription = this.landingSvc.postGasLetter(formValue).subscribe(result => {
        const refid = result.data.id;
        this.message = this.lookupSvc.messages.find(x =>
          x.itemName.toUpperCase() === this.gasLetterForm.getRawValue().serviceState.id.toUpperCase() &&
          x.messageType.toUpperCase() === this.messageTypeEnum.Letter).message;
          this.message = this.lookupSvc.messages.find(x =>
            x.itemName.toUpperCase() === this.gasLetterForm.getRawValue().serviceState.id.toUpperCase() &&
            x.messageType.toUpperCase() === this.messageTypeEnum.Letter).message;
        this.message = this.message.replace('##', this.lookupSvc.companies[this.serviceAddress.state.short_name].name);
        this.message = this.message.replace('XXX', this.serviceAddress.formatted_address);
        this.message = this.message.replace('XX', refid);
        $('#acknowledge_validation_modal').modal();
      }, result => { swal(this.constantsSvc.errorSwal1, this.constantsSvc.contactCustomerCareSwal, this.constantsSvc.errorSwal); });
    }
  }

  // used to get formatted address
  getMailingFormattedAddress() {
    const model = this.gasLetterForm.getRawValue();
    if (!this.isPoBox) {
      return model.mailingAddress + ', ' + model.applicantCity.itemName + ' ,' + model.applicantState.id + ' ' + model.applicantZip;
    } else {
      return 'PO Box ' + model.poBoxNumber + ', ' + model.applicantCity.itemName + ' ,' +
       model.applicantState.id + ' ' + model.applicantZip;
    }
  }

  // Applicant address same as service address
  mailingAddressToggle(eve: any) {
    const mailingAddressSame = <FormControl>(this.gasLetterForm.get('mailingAddressSame'));
    if (mailingAddressSame.value) {
      this.gasLetterForm.controls['isPoBox'].patchValue(false);
      this.gasLetterForm.controls['poBoxNumber'].clearValidators();
      this.gasLetterForm.controls['poBoxNumber'].setValue('');
      this.isPoBox = false;
    }
    this.cloneServiceAddress(mailingAddressSame.value);
  }

  // PO box toggle
  poBoxToggle(e) {
    this.isPoBox = this.gasLetterForm.controls['isPoBox'].value;
    if (this.isPoBox) {
      this.gasLetterForm.controls['mailingAddressSame'].setValue(false);
      this.gasLetterForm.controls['poBoxNumber'].setValidators([Validators.required,
        Validators.pattern(this.constantsSvc.numericPatternwithZero)]);
        this.gasLetterForm.controls['mailingAddress'].clearValidators();
        this.gasLetterForm.controls['mailingAddress'].patchValue('');
    } else {
      this.gasLetterForm.controls['mailingAddress'].patchValue(null);
      this.gasLetterForm.controls['mailingAddress'].setValidators([Validators.required,
        Validators.pattern(this.constantsSvc.alphaNumericPattern)]);
      this.isPoBox = false;
      // enable address fields when po box is deselected
      this.gasLetterForm.controls['mailingAddress'].enable();
      this.gasLetterForm.controls['applicantZip'].enable();
      this.gasLetterForm.controls['poBoxNumber'].clearValidators();
      this.gasLetterForm.controls['poBoxNumber'].setValue('');
    }
  }
  getAddressObjFromForm() {
    const model = this.gasLetterForm.getRawValue();
    if (this.gasLetterForm.controls['isPoBox'].value) { return null; }
    return {
      street: (model.mailingAddress.replace(',', '')).toUpperCase(), city: model.applicantCity.itemName.toUpperCase(),
      state: model.applicantState.id.toUpperCase(), zip: model.applicantZip
    };
  }

  // used to clone service address
  cloneServiceAddress(same: boolean) {
    if (same) {
      this.gasLetterForm.patchValue({
        mailingAddress: this.gasLetterForm.controls['projectAddress'].value,
        applicantZip: this.gasLetterForm.controls['zip'].value,
        applicantCity: this.gasLetterForm.controls['serviceCity'].value ? this.gasLetterForm.controls['serviceCity'].value : [],
        applicantState: this.gasLetterForm.controls['serviceState'].value ? this.gasLetterForm.controls['serviceState'].value : [],
      });
      this.gasLetterForm.controls['mailingAddress'].disable();
      this.gasLetterForm.controls['applicantZip'].disable();
      this.addressToValidate = this.getAddressObjFromForm();
    } else {
      this.gasLetterForm.patchValue({ mailingAddress: '', applicantZip: '', applicantCity: [], applicantState: [], });
      this.gasLetterForm.controls['mailingAddress'].enable();
      this.gasLetterForm.controls['applicantZip'].enable();
      this.gasLetterForm.updateValueAndValidity();
      this.addressToValidate = null;
    }
  }
  //#endregion
    closeAcknowledgement() { // to close the acknowledgement pop-up
       $('#acknowledge_validation_modal').modal('hide');
     this.skipCanDeactivate = true;
    this.router.navigate(['/', this.sessionSvc.paramLdc.toLowerCase(), 'home']);
  }
}

export enum LetterActiveViewEnum {
  Location = 0,
  LetterForm
}

